'use client';

import type { QueryKey } from '@tanstack/react-query';

/*
 * Id params below accept `null` as well as `undefined`. Several screens hold
 * their route id as `string | null` (`useIdParam`), and both spellings hash to
 * the same key anyway: TanStack hashes with `JSON.stringify`, which turns an
 * `undefined` ARRAY member into `null`. Widening the signature therefore keeps
 * one cache entry per id instead of forcing `?? undefined` at every call site.
 */
import { accountingKeys } from '@/lib/accounting-api';

/**
 * One key factory per module, for the whole app outside Accounting.
 *
 * `lib/accounting-api.ts` has had `accountingKeys` since E15 and it works: a
 * write anywhere in that module can name the lists it affects instead of
 * spelling a tuple by hand. Everywhere else the keys were spelled by hand at
 * ~130 call sites, and the audit found exactly the failures that predicts:
 *
 *   - `['admin','subscription']` (the three subscription-detail writes) never
 *     matches `['admin','subscriptions', page]` (the monitor list), because
 *     TanStack compares key elements with deep equality and
 *     'subscription' !== 'subscriptions'. Three invalidations that look right
 *     have never refreshed the screen that shows their result.
 *   - `GET /verification/:id` is cached twice, as `['verification', orgId]` and
 *     as `['agreement', mode, orgId]`, and each screen invalidates only its own
 *     spelling.
 *   - `['mail-plan-options']` is a second root for the same fetcher as
 *     `['mail-plans','options']`, so no `['mail-plans']` prefix reaches it.
 *
 * A factory cannot fix a duplicate key on its own, so where the app really does
 * hold one resource under two roots BOTH spellings live here, next to each
 * other, and the invalidation map (`lib/use-app-mutation.ts`) names both.
 *
 * RULE FOR EVERY EDIT TO THIS FILE: the tuples below are BYTE-IDENTICAL to what
 * the screens pass to `useQuery` today. They are not a redesign of the key
 * space. Changing a literal here does not move a cache entry, it ORPHANS one:
 * the screen keeps reading the old key and the invalidation quietly stops
 * landing. Adopt first, normalise later (and only with every reader in the same
 * commit).
 *
 * Prefix matching is the whole point: `invalidateQueries({ queryKey: ['stock'] })`
 * hits `['stock','list',…]` and `['stock','detail',id]` alike. So each factory
 * exposes the ROOTS a mutation should name (`all`, `lists()`, `details()`) as
 * well as the exact keys a query subscribes to.
 */

/* ------------------------------------------------------------------ */
/* Accounting (re-exported, not redefined)                            */
/* ------------------------------------------------------------------ */

/**
 * The precedent, re-exported so there is ONE import for keys.
 *
 * Accounting caches four FOREIGN tables under its own `['accounting', …]`
 * prefix — suppliers, customers, companies and brands (see
 * `components/accounting/documents/partner-select.tsx:27-33` and
 * `components/accounting/scope.tsx:74`). A supplier write in the Company module
 * therefore has to invalidate an accounting key, which is why the invalidation
 * map imports this alongside the module factories below.
 */
export { accountingKeys };

/* ------------------------------------------------------------------ */
/* Session / permissions / tenant                                      */
/* ------------------------------------------------------------------ */

/**
 * Re-exported from their owning hooks rather than re-spelled, so there is
 * exactly one definition of the three highest-traffic keys in the app.
 * `['organizations','access-state']` in particular is what `AuthBoundary`
 * routes every route on (`lib/auth-routes.ts:290-315`), and it is derived from
 * `organization.status` + `subscription.status`
 * (`apps/api/src/organizations/organizations.service.ts:21-62`) — both of which
 * are written by four other modules.
 */
/*
 * These three used to live on the hooks that read them and were re-exported
 * through here. That inverted dependency broke every suite that does
 * `vi.mock('@/lib/use-auth-session')`: the mock replaces the whole module, the
 * constant vanishes with it, and any screen importing a key from this registry
 * died with "No SESSION_QUERY_KEY export is defined on the mock" — in tests
 * that have nothing to do with sessions.
 *
 * A key is data, not behaviour, so it belongs in the registry and the hooks
 * import it from here. Tuples are unchanged, so no cache entry moves.
 */
export const SESSION_QUERY_KEY = ['auth', 'me'] as const;
export const ACCESS_STATE_QUERY_KEY = ['organizations', 'access-state'] as const;
export const MY_PERMISSIONS_QUERY_KEY = ['me', 'permissions'] as const;

/**
 * Keys the auth bootstrap owns. Grouped so a login / logout / OTP write can
 * declare all three at once instead of seeding one and hoping the cache was
 * cleared for the other two.
 */
export const sessionKeys = {
  /** Signed-in principal (`authSessions.me`). */
  me: SESSION_QUERY_KEY,
  /** Tenant lifecycle gate. Stale here means the app gates or opens the wrong route. */
  accessState: ACCESS_STATE_QUERY_KEY,
  /** Effective permission set behind every create / edit / delete affordance. */
  permissions: MY_PERMISSIONS_QUERY_KEY,
  /** Pre-auth workspace resolve for the current subdomain (`lib/use-tenant.ts:50`). */
  tenant: (slug: string | null | undefined) => ['tenant', slug] as QueryKey,
} as const;

/**
 * `['organizations', …]` — the tenant-facing org reads. Two entries only
 * (access-state and the assigned price), which is why `all` is a safe root: a
 * lifecycle write can name it without dragging any other module along.
 */
export const organizationKeys = {
  all: ['organizations'] as const,
  /** Same tuple as `ACCESS_STATE_QUERY_KEY`; kept here so the org root reads whole. */
  accessState: ACCESS_STATE_QUERY_KEY,
  /**
   * The org's assigned monthly price, shown on the add-card gate
   * (`app/billing/payment-method/page.tsx:63`). Written by Super Admin pricing
   * (`apps/api/src/pricing/pricing.service.ts:104`), i.e. from a different app.
   */
  subscriptionPrice: (organizationId: string | null | undefined) =>
    ['organizations', 'subscription-price', organizationId] as QueryKey,
  /**
   * Prefix over the price of every cached org. A pricing write happens in the
   * Super Admin console, which does not know the tenant's org id, so the map
   * names this root rather than a key it cannot build.
   */
  allSubscriptionPrices: () => ['organizations', 'subscription-price'] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Shared header filter scope (company / brand / market)              */
/* ------------------------------------------------------------------ */

/**
 * `['filter-scope', …]` — the ONE catalogue behind the shared Company / Brand /
 * Market bar (`components/filters/filter-scope.tsx`).
 *
 * A NEW root, not a rename of anything: the eight list screens keep their own
 * facet keys (`stockKeys.filters()`, `offerKeys.facets()`, …) because each also
 * feeds a filter this bar does not draw (warehouses, suppliers) and because
 * several of them read NAMES where this one reads ids. This entry is the id +
 * name pairing the bar needs to resolve a stored uuid back to a label, and the
 * `company` / `brand` / `market` rows of the invalidation map name it alongside
 * the per-screen facet keys they already name.
 */
export const filterScopeKeys = {
  all: ['filter-scope'] as const,
  /** Companies, brands and markets of the org, each with its id, name and owning company. */
  options: () => ['filter-scope', 'options'] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Stock                                                              */
/* ------------------------------------------------------------------ */

/** Filter object the Stock grid keys on (`app/stock/page.tsx:99`). */
/**
 * 🔴 WEST-779 — the four scope keys are back, with the controls that set them.
 *
 * 🔴 WEST-746 took both away together and that pairing is the rule worth
 * keeping: a key here with no control on screen is a filter the reader can
 * neither see nor clear. QA (2026-10-01) says the removal was raised by
 * mistake, so both halves return. ⚠ This puts the screen back against ✅
 * `CF-51-FILTER`; Defer Log `CF-51-FILTER2`.
 */
export interface StockListParams {
  deferredQ: string;
  company: string;
  brand: string;
  market: string;
  warehouse: string;
}

/**
 * `['stock', …]`.
 *
 * `all` is the right root for anything that moves a quantity: an approved
 * adjustment writes `stock_by_warehouse.qty_on_hand` AND appends a
 * `stock_movement` row in one transaction
 * (`apps/api/src/inventory-adjustments/inventory-adjustments.service.ts:426-452`),
 * so list, detail, movements and adjustments all go stale together and there is
 * nothing else under this root to over-refetch.
 */
export const stockKeys = {
  all: ['stock'] as const,
  /** Company / brand / market / warehouse dropdowns of the list header. */
  filters: () => ['stock', 'filters'] as QueryKey,
  /** Prefix over every filtered page of the grid. */
  lists: () => ['stock', 'list'] as QueryKey,
  list: (params: StockListParams) => ['stock', 'list', params] as QueryKey,
  /** Prefix over every cached stock row, for a write that has no single id. */
  details: () => ['stock', 'detail'] as QueryKey,
  detail: (id: string | null | undefined) => ['stock', 'detail', id] as QueryKey,
  /** Prefix over both ledgers (org-wide and per-row). */
  movements: () => ['stock', 'movements'] as QueryKey,
  /** Org-wide ledger, Movements tab of the list screen. */
  allMovements: () => ['stock', 'movements', 'all'] as QueryKey,
  recordMovements: (id: string | null | undefined) => ['stock', 'movements', id] as QueryKey,
  adjustments: (id: string | null | undefined) => ['stock', 'adjustments', id] as QueryKey,
  /** Prefix over the adjustment history of every cached row. */
  allAdjustments: () => ['stock', 'adjustments'] as QueryKey,
  /**
   * E12 US-56 purchase requirements: what the FIFO engine says to buy per
   * (variation, warehouse). Prefix, so a recompute or a PO write can drop every
   * scenario and page at once.
   */
  allPurchaseRequirements: () => ['stock', 'purchase-requirements'] as QueryKey,
  purchaseRequirements: (params: unknown) => ['stock', 'purchase-requirements', params] as QueryKey,
  /**
   * Variations, companies, brands, warehouses and suppliers for Add Stock
   * Record and Create PO. Same catalogue as `purchaseOrderKeys.newOptions()`
   * under a second name, so a variation or supplier write must name both.
   */
  formOptions: () => ['stock', 'form-options'] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Purchase orders                                                    */
/* ------------------------------------------------------------------ */

/** Filter object the PO list keys on (`app/purchase-orders/page.tsx:74`). */
export interface PurchaseOrderListParams {
  deferredQ: string;
  company: string;
  brand: string;
  market: string;
  supplier: string;
}

/**
 * `['purchase-orders', …]`.
 *
 * A status write (approve / cancel / reject) changes the row, the list, the
 * approval log carried by the detail and the facet counts, so `all` is the
 * honest root here — four entries, all of them genuinely affected.
 */
export const purchaseOrderKeys = {
  all: ['purchase-orders'] as const,
  filters: () => ['purchase-orders', 'filters'] as QueryKey,
  lists: () => ['purchase-orders', 'list'] as QueryKey,
  list: (params: PurchaseOrderListParams) => ['purchase-orders', 'list', params] as QueryKey,
  details: () => ['purchase-orders', 'detail'] as QueryKey,
  /** One PO plus its receipts and shipments (three endpoints, one query). */
  detail: (id: string | null | undefined) => ['purchase-orders', 'detail', id] as QueryKey,
  /**
   * WEST-814 History tab. Nested UNDER `detail(id)` on purpose: every write on
   * the detail screen invalidates the PO root, so an Approve or a receipt
   * refreshes the history without naming it.
   */
  statusHistory: (id: string | null | undefined) =>
    ['purchase-orders', 'detail', id, 'status-history'] as QueryKey,
  /** Suppliers / companies / brands / warehouses / variations for the New PO dialog. */
  newOptions: () => ['purchase-orders', 'new-options'] as QueryKey,
  /**
   * Carrier list for the shipment dialog. A hard-coded constant today
   * (`components/purchase-orders/data.ts:559-564`), not a server read, so it
   * needs a key slot and no invalidation edge.
   */
  carriers: () => ['purchase-orders', 'carriers'] as QueryKey,
  /**
   * The To purchase tab of the Purchase Order screen (WEST-690).
   *
   * ⛔ IT DELIBERATELY KEYS UNDER THE **STOCK** ROOT, not this one, and the
   * name here is an alias so nobody in this module has to know that.
   *
   * The tab reads `GET /stock/purchase-requirements`. Two writes already
   * invalidate that resource by its stock prefix — `recomputeForecasts` names
   * `stockKeys.allPurchaseRequirements()`, and so does the Mail Plan forecast
   * path — and neither of them is going to learn a second root. A key of
   * `['purchase-orders','to-purchase',…]` would look right, cache correctly and
   * then never refresh after the buyer pressed Recompute: the screen would sit
   * on pre-recompute numbers with a green toast on top saying the engine had
   * just run. One resource, one root; this entry only gives it the name the
   * module that reads it would look for.
   */
  toPurchase: (params: unknown) => stockKeys.purchaseRequirements(params),
} as const;

/* ------------------------------------------------------------------ */
/* Inventory (read-only module)                                       */
/* ------------------------------------------------------------------ */

/**
 * `['inventory', …]`.
 *
 * The Inventory catalog performs no writes at all — it is `productVariations.list`
 * plus `offers.list` (`components/inventory/data.ts:186-189`). It is a pure
 * victim of product-variation and offer writes, which is why it appears on the
 * RIGHT of several rows in the invalidation map and never on the left.
 *
 * Note the argument ORDER: this list spreads its filters as
 * `company, brand, market, q` while `productKeys.list` spreads them as
 * `q, company, brand, market`. Both are reproduced verbatim.
 */
export const inventoryKeys = {
  all: ['inventory'] as const,
  filterOptions: () => ['inventory', 'filter-options'] as QueryKey,
  lists: () => ['inventory', 'list'] as QueryKey,
  list: (company: string, brand: string, market: string, deferredQ: string) =>
    ['inventory', 'list', company, brand, market, deferredQ] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Product variations                                                 */
/* ------------------------------------------------------------------ */

/**
 * `['product-variations', …]`.
 *
 * `productUniverses` and `productTypes` live OUTSIDE this root on purpose —
 * that is how the screens spell them (`components/product-variations/variation-form.tsx:90-91`),
 * so the form's own `['product-variations']` invalidate has never reached them
 * and the two inline-add controls fall back to a local refetch. They are listed
 * here so a write can name them explicitly.
 */
export const productVariationKeys = {
  all: ['product-variations'] as const,
  filterOptions: () => ['product-variations', 'filter-options'] as QueryKey,
  lists: () => ['product-variations', 'list'] as QueryKey,
  list: (deferredQ: string, company: string, brand: string, market: string) =>
    ['product-variations', 'list', deferredQ, company, brand, market] as QueryKey,
  details: () => ['product-variations', 'detail'] as QueryKey,
  /** Detail screen AND the edit-form prefill share this entry. */
  detail: (id: string | null | undefined) => ['product-variations', 'detail', id] as QueryKey,
  /** Owning family plus its variations, for the detail header and derived names. */
  family: (familyId: string | null | undefined) =>
    ['product-variations', 'family', familyId] as QueryKey,
  families: () => ['product-variations', 'family'] as QueryKey,
} as const;

/** Product universe dictionary. Sits outside the `['product-variations']` prefix. */
export const productUniverseKeys = {
  all: ['product-universes'] as const,
} as const;

/** Product type dictionary. Same prefix problem as `productUniverseKeys`. */
export const productTypeKeys = {
  all: ['product-types'] as const,
} as const;

/* ------------------------------------------------------------------ */
/* Product families ("Products")                                      */
/* ------------------------------------------------------------------ */

/**
 * `['products', …]`.
 *
 * This root holds two different things: the family list/detail, and five
 * dropdown catalogues the family FORM reads (companies, brands, markets,
 * suppliers, categories). A company or supplier write in the Company module
 * therefore has to name `productKeys.companies()` / `productKeys.suppliers()`
 * even though it changed nothing about a product family.
 */
export const productKeys = {
  all: ['products'] as const,
  lists: () => ['products', 'list'] as QueryKey,
  list: (deferredQ: string, company: string, brand: string, market: string) =>
    ['products', 'list', deferredQ, company, brand, market] as QueryKey,
  details: () => ['products', 'detail'] as QueryKey,
  detail: (id: string | null | undefined) => ['products', 'detail', id] as QueryKey,
  /** Company catalogue shared by the list filter and the family form. */
  companies: () => ['products', 'companies'] as QueryKey,
  /** Prefix over every company's brand list. */
  allBrands: () => ['products', 'brands'] as QueryKey,
  brands: (companyId: string | null | undefined) => ['products', 'brands', companyId] as QueryKey,
  allMarkets: () => ['products', 'markets'] as QueryKey,
  markets: (companyId: string | null | undefined) => ['products', 'markets', companyId] as QueryKey,
  allSuppliers: () => ['products', 'suppliers'] as QueryKey,
  suppliers: (companyId: string | null | undefined) =>
    ['products', 'suppliers', companyId] as QueryKey,
  /** Dictionary behind the inline "Add new Category" control on the family form. */
  categories: () => ['products', 'categories'] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Bundles                                                            */
/* ------------------------------------------------------------------ */

/**
 * `['bundles', …]`.
 *
 * A bundle price write is never local: `repriceBundle` rewrites
 * `bundle.original_price`, `bundle.display_price` and every `offer_line` of the
 * bundle (`apps/api/src/offers/offers.service.ts:1424-1447`), and the owning
 * offer's detail DTO carries its bundles.
 */
export const bundleKeys = {
  all: ['bundles'] as const,
  /** Same tuple as `list()`: this list takes no filters, so the root IS the key. */
  lists: () => ['bundles', 'list'] as QueryKey,
  list: () => ['bundles', 'list'] as QueryKey,
  details: () => ['bundles', 'detail'] as QueryKey,
  /** One bundle with its lines; also the pricing form's prefill. */
  detail: (id: string | null | undefined) => ['bundles', 'detail', id] as QueryKey,
  /**
   * 🔷 `WEST-647` — the change history of one bundle. Separate from `detail`
   * because a save has to invalidate BOTH: the detail for the new numbers and
   * this for the row describing the move.
   */
  auditLog: (id: string | null | undefined) => ['bundles', 'audit-log', id] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Orders                                                             */
/* ------------------------------------------------------------------ */

export const orderKeys = {
  all: ['orders'] as const,
  lists: () => ['orders', 'list'] as QueryKey,
  list: (deferredQ: string, status: string, payment: string) =>
    ['orders', 'list', deferredQ, status, payment] as QueryKey,
  details: () => ['orders', 'detail'] as QueryKey,
  detail: (id: string | null | undefined) => ['orders', 'detail', id] as QueryKey,
  payments: (id: string | null | undefined) => ['orders', 'payments', id] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Customers                                                          */
/* ------------------------------------------------------------------ */

/**
 * `['customers', …]`.
 *
 * `all` is the right root for any order write, not an over-reach: every order
 * path funnels into `refreshCustomerRollup`, which rewrites
 * `customer.total_orders`, `total_amount` and `last_purchase_date`
 * (`apps/api/src/customer-orders/customer-orders.service.ts:646`), and the
 * Activity and Mail Plans tabs are derived from `customer_order` rows too
 * (`apps/api/src/customers/timeline.service.ts:100-128`).
 */
export const customerKeys = {
  all: ['customers'] as const,
  lists: () => ['customers', 'list'] as QueryKey,
  list: (deferredQ: string, status: string, blacklist: string) =>
    ['customers', 'list', deferredQ, status, blacklist] as QueryKey,
  details: () => ['customers', 'detail'] as QueryKey,
  detail: (id: string | null | undefined) => ['customers', 'detail', id] as QueryKey,
  /** Mail Plans tab: `key_code` enrolments joined to `mail_plan.status`. */
  mailPlans: (customerId: string | null | undefined) =>
    ['customers', 'mail-plans', customerId] as QueryKey,
  allMailPlans: () => ['customers', 'mail-plans'] as QueryKey,
  orders: (customerId: string | null | undefined) =>
    ['customers', 'orders', customerId] as QueryKey,
  allOrders: () => ['customers', 'orders'] as QueryKey,
  /** Timeline merging comments, enrolments, orders and the blacklist toggle. */
  activity: (customerId: string | null | undefined) =>
    ['customers', 'activity', customerId] as QueryKey,
  allActivity: () => ['customers', 'activity'] as QueryKey,
  /**
   * Saved customer lists (🔷 PO 2026-08-25). No longer an orphan: bulk import
   * moved off the Mail Plan and onto the Customers screen, so this key finally
   * has both a reader (the Imported list tab) and a writer that persists.
   */
  imported: () => ['customers', 'imported'] as QueryKey,
  importedDetail: (id: string | null | undefined) => ['customers', 'imported', id] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Offers                                                             */
/* ------------------------------------------------------------------ */

/**
 * `['offers', …]`.
 *
 * `offer.offer_status` (`apps/api/src/offers/offers.service.ts:645`) is the gate
 * every downstream picker filters on, so an approve / reject here is one of the
 * widest cross-module writes in the app — see the `offer.status` row of the
 * invalidation map.
 */
export const offerKeys = {
  all: ['offers'] as const,
  /** Company / brand / market name lists for the three list-screen dropdowns. */
  facets: () => ['offers', 'facets'] as QueryKey,
  lists: () => ['offers', 'list'] as QueryKey,
  list: (deferredQ: string, company: string, brand: string, market: string, status: string) =>
    ['offers', 'list', deferredQ, company, brand, market, status] as QueryKey,
  details: () => ['offers', 'detail'] as QueryKey,
  detail: (id: string | null | undefined) => ['offers', 'detail', id] as QueryKey,
  /** Wizard catalogues: offer types, offer forms, brands, companies, variations. */
  options: () => ['offers', 'options'] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Mail plans                                                         */
/* ------------------------------------------------------------------ */

/**
 * `['mail-plans', …]`.
 *
 * The Calendar tab caches under `['mail-plans','calendar', filters]`, a SIBLING
 * of 'list' that no `['mail-plans','list']` prefix reaches — the reason
 * `app/mail-plans/detail/page.tsx:112-122` already widened to the module root.
 * Keep naming `all` for status writes; the narrower roots are for the pickers.
 */
export const mailPlanKeys = {
  all: ['mail-plans'] as const,
  /**
   * Org-wide catalogues for the list filters and the detail dialogs: brands,
   * companies, markets, families, APPROVED offers, offer types, customers.
   */
  options: () => ['mail-plans', 'options'] as QueryKey,
  callCenters: () => ['mail-plans', 'call-centers'] as QueryKey,
  lists: () => ['mail-plans', 'list'] as QueryKey,
  list: (deferredQ: string, company: string, market: string, drawer: unknown) =>
    ['mail-plans', 'list', deferredQ, company, market, drawer] as QueryKey,
  details: () => ['mail-plans', 'detail'] as QueryKey,
  detail: (id: string | null | undefined) => ['mail-plans', 'detail', id] as QueryKey,
  /** Keyed on a filter OBJECT; pass the same object identity the screen holds. */
  calendar: (filters: unknown) => ['mail-plans', 'calendar', filters] as QueryKey,
  allAnalytics: () => ['mail-plans', 'analytics'] as QueryKey,
  /**
   * WEST-793 (US-41B) organization Dashboard. Nested under `allAnalytics()` on
   * purpose: it aggregates the same `customer_order` rows the per-plan analytics
   * read, so every write that already refreshes those (`order.status`, and every
   * `mail-plan*` domain through `mailPlanKeys.all`) refreshes the Dashboard too,
   * with no new entry in `WRITE_DOMAIN_KEYS` to forget.
   */
  dashboard: (query: unknown) => ['mail-plans', 'analytics', 'dashboard', query] as QueryKey,
  /** Computed by the server from `customer_order` rows carrying this mail plan id. */
  analytics: (planId: string | null | undefined) => ['mail-plans', 'analytics', planId] as QueryKey,
  statusHistory: (planId: string | null | undefined) =>
    ['mail-plans', 'status-history', planId] as QueryKey,
  allStockForecast: () => ['mail-plans', 'stock-forecast'] as QueryKey,
  /**
   * E12 US-56 per-SKU forecast of ONE plan. Keyed on the scenario too: the
   * confirmed-mail-plans and all-mail-plans walks are different numbers, and
   * caching them under one key would show one while the toggle says the other.
   */
  stockForecast: (planId: string | null | undefined, scenario: string) =>
    ['mail-plans', 'stock-forecast', planId, scenario] as QueryKey,
  allAudience: () => ['mail-plans', 'audience'] as QueryKey,
  /** Enrolment / blacklisted / unknown counters on the Targeting tab. */
  audience: (planId: string | null | undefined) => ['mail-plans', 'audience', planId] as QueryKey,
  brands: () => ['mail-plans', 'brands'] as QueryKey,
  families: (brandId: string | null | undefined) => ['mail-plans', 'families', brandId] as QueryKey,
  allOffers: () => ['mail-plans', 'offers'] as QueryKey,
  /** APPROVED offers of the chosen brand. Empties the moment an offer leaves `approved`. */
  offers: (brandId: string | null | undefined) => ['mail-plans', 'offers', brandId] as QueryKey,
  allWarehouses: () => ['mail-plans', 'warehouses'] as QueryKey,
  /*
   * `keepId` is part of the key because the two callers ask for different
   * lists: the create wizard wants the ACTIVE warehouses of the company, and
   * the edit dialog wants those plus the one this plan already points at, which
   * may since have been deactivated (WEST-664). Sharing one key would serve the
   * wizard's list to the dialog and make the plan's own warehouse vanish from
   * its own form.
   */
  warehouses: (companyId: string | null | undefined, keepId?: string | null) =>
    ['mail-plans', 'warehouses', companyId, keepId ?? null] as QueryKey,
} as const;

/**
 * SECOND ROOT for the same fetcher as `mailPlanKeys.options()`
 * (`components/mail-plans/detail-dialogs.tsx:465`). No `['mail-plans']` prefix
 * reaches it, so anything that invalidates the options catalogue must name this
 * too. Harmless today only because its single reader is never mounted; declared
 * here so the invalidation map can cover it rather than pretend it is gone.
 */
export const mailPlanOptionsOrphanKey = ['mail-plan-options'] as const;

/* ------------------------------------------------------------------ */
/* Company (companies, brands, markets, warehouses, suppliers)        */
/* ------------------------------------------------------------------ */

/** The four entity tabs of the company detail screen (`components/company/entities.tsx:48`). */
export type CompanyEntityKey = 'brand' | 'market' | 'warehouse' | 'supplier';

/**
 * `['company', …]`.
 *
 * FOUR incompatible shapes share position 1 of this root: `['company','list',q]`,
 * `['company','detail',id]`, `['company', companyId, entity, q]` and
 * `['company', companyId, 'options', key]`. A companyId sits exactly where the
 * literals 'list' and 'detail' sit, so NO prefix short of `['company']` covers
 * the tab keys — which is why the invalidation map names `companyKeys.all` for
 * every brand / market / warehouse / supplier write. That is deliberate, not
 * laziness: `replaceMarketLinks` rewrites the whole `brand_market` set in the
 * same transaction as the brand row (`apps/api/src/brands/brands.service.ts:288`
 * -> `:526-540`), so the OTHER tab is stale from the same write.
 */
export const companyKeys = {
  all: ['company'] as const,
  lists: () => ['company', 'list'] as QueryKey,
  list: (deferredQ: string) => ['company', 'list', deferredQ] as QueryKey,
  details: () => ['company', 'detail'] as QueryKey,
  detail: (id: string | null | undefined) => ['company', 'detail', id] as QueryKey,
  /**
   * 🔴 WEST-719 — what one warehouse still carries, read only on the edge where
   * it is about to be switched off. Under `company` so the existing
   * invalidation map reaches it when a warehouse changes.
   */
  warehouseLinkedRecords: (warehouseId: string) =>
    ['company', 'warehouse-linked-records', warehouseId] as QueryKey,
  /** Prefix over one tab's rows regardless of its search term. */
  entityScope: (companyId: string, entity: CompanyEntityKey) =>
    ['company', companyId, entity] as QueryKey,
  entity: (companyId: string, entity: CompanyEntityKey, deferredQ: string) =>
    ['company', companyId, entity, deferredQ] as QueryKey,
  /**
   * Picker lists a company-detail form needs: 'markets' | 'brands' | 'warehouses'.
   *
   * `held` is part of the key since WEST-486: the list now depends on what the
   * record being edited holds (an inactive row it points at is kept, every other
   * inactive row is filtered out), so two forms on the same tab must not share
   * one cache entry. Sorted, because the same set in a different order is the
   * same list.
   */
  options: (companyId: string, source: string, held: readonly string[] = []) =>
    ['company', companyId, 'options', source, [...held].sort().join(',')] as QueryKey,
  allOptions: (companyId: string) => ['company', companyId, 'options'] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Users & permissions                                                */
/* ------------------------------------------------------------------ */

/**
 * `['users', …]`.
 *
 * The one module the audit found already correct: every write invalidates the
 * bare `['users']` prefix, which is what keeps the list, the detail screen AND
 * the edit dialog's prefill consistent — the latter caches the SAME
 * `GET /users/:id` under a different shape, `['users', id, 'detail']`
 * (`components/users/user-form-dialog.tsx:74`). Both spellings are below.
 *
 * `list(q)` puts the search term at position 1, where `'detail'` also sits.
 * Searching for the literal string "detail" collides with `details()`; harmless
 * (both are user lists) but do not build a narrower prefix on position 1.
 */
export const userKeys = {
  all: ['users'] as const,
  list: (deferredQ: string) => ['users', deferredQ] as QueryKey,
  details: () => ['users', 'detail'] as QueryKey,
  detail: (id: string | null | undefined) => ['users', 'detail', id] as QueryKey,
  /** The edit dialog's second spelling of the same resource. Do not add a third. */
  formPrefill: (id: string | null | undefined) => ['users', id, 'detail'] as QueryKey,
} as const;

/** `module_catalog` behind the permission matrix. Static; nothing in the app writes it. */
export const permissionKeys = {
  all: ['permissions'] as const,
  modules: () => ['permissions', 'modules'] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Notifications                                                      */
/* ------------------------------------------------------------------ */

/**
 * `['notifications', …]` holds ONLY the per-user preference switches.
 *
 * The approval feed and the unread pill are accounting-owned
 * (`accountingKeys.notifications` / `accountingKeys.unreadNotifications`),
 * because the rows are inserted by `apps/api/src/accounting/invoice.service.ts:2315`
 * and `credit.service.ts:1456`. Do not mint a `['notifications','feed']`.
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  preferences: () => ['notifications', 'preferences'] as QueryKey,
  /** Every page of the feed, whatever it is filtered to. */
  feeds: () => ['notifications', 'feed'] as QueryKey,
  feed: (params: { status?: string; module?: string; page: number; pageSize?: number }) =>
    ['notifications', 'feed', params] as QueryKey,
  /**
   * The bell's badge. Its own entry rather than a field read off a feed page:
   * the bell renders on screens that never mount the feed, and it polls on a
   * cadence the list must not inherit.
   */
  unread: () => ['notifications', 'unread'] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Subscription & billing (tenant side)                               */
/* ------------------------------------------------------------------ */

/** `['subscription', …]` — the tenant's own plan, current row and payment history. */
export const subscriptionKeys = {
  all: ['subscription'] as const,
  /** Purchasable plan catalogue (one plan per org, E01-C21). */
  plans: () => ['subscription', 'plans'] as QueryKey,
  /** Current subscription + plan name; polled on the payment-return screen. */
  me: () => ['subscription', 'me'] as QueryKey,
  allPayments: () => ['subscription', 'payments'] as QueryKey,
  payments: (subscriptionId: string | null | undefined) =>
    ['subscription', 'payments', subscriptionId] as QueryKey,
} as const;

/**
 * `['billing', …]` — the card Stripe holds for this org.
 *
 * Deliberately NOT under `['subscription']`, which is how the app spells it and
 * why the one invalidate on /plans (`page.tsx:95`) has never reached it. Named
 * separately in every billing row of the invalidation map.
 */
export const billingKeys = {
  all: ['billing'] as const,
  paymentMethod: () => ['billing', 'payment-method'] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Verification & service agreement (tenant side)                     */
/* ------------------------------------------------------------------ */

/**
 * `['verification', orgId]` — the org's current verification request and its
 * documents. In SESSION mode this is the same `GET /verification/:id` response
 * that `agreementKeys.state('session', orgId)` holds under a different root, so
 * every write on either screen has to name both. See `agreementKeys`.
 */
export const verificationKeys = {
  all: ['verification'] as const,
  detail: (orgId: string | null | undefined) => ['verification', orgId] as QueryKey,
} as const;

/** Which client the service-agreement screen is talking to. */
export type AgreementMode = 'link' | 'session';

/**
 * `['agreement', mode, orgId ?? 'link']` — the service-agreement screen's own
 * copy of the verification state.
 *
 * SECOND ROOT for one server resource, kept because that is what
 * `app/service-agreement/page.tsx:110` subscribes to. The `?? 'link'` fallback
 * is part of the literal: in link mode there is no org id, only a token.
 */
export const agreementKeys = {
  all: ['agreement'] as const,
  state: (mode: AgreementMode, orgId: string | null | undefined) =>
    ['agreement', mode, orgId ?? 'link'] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Super Admin console                                                */
/* ------------------------------------------------------------------ */

/**
 * `['admin', …]`.
 *
 * READ THE SINGULAR/PLURAL NOTE BEFORE ADDING A KEY. The subscription DETAIL
 * screen caches under `['admin','subscription', …]` while the subscription
 * MONITOR list and the dashboard tile cache under `['admin','subscriptions', …]`.
 * TanStack matches keys element by element with deep equality, so
 * `['admin','subscription']` has never once refreshed the monitor — three live
 * invalidations (activate, deactivate, set price) silently do nothing. Both
 * roots are exported below and the invalidation map names BOTH for every
 * subscription write. Do not "tidy" one of them away without moving its readers
 * in the same commit.
 */
export const adminKeys = {
  all: ['admin'] as const,
  /** Org list behind the Dashboard cards AND the Organizations table (one entry). */
  organizations: () => ['admin', 'organizations'] as QueryKey,
  orgOverview: (id: string | null | undefined) => ['admin', 'org-overview', id] as QueryKey,
  allOrgOverviews: () => ['admin', 'org-overview'] as QueryKey,
  orgStructure: (id: string | null | undefined) => ['admin', 'org-structure', id] as QueryKey,
  /** SINGULAR root: the per-org subscription detail, its price and its payments. */
  subscription: () => ['admin', 'subscription'] as QueryKey,
  subscriptionDetail: (orgId: string | null | undefined) =>
    ['admin', 'subscription', 'detail', orgId] as QueryKey,
  subscriptionPrice: (orgId: string | null | undefined) =>
    ['admin', 'subscription', 'price', orgId] as QueryKey,
  subscriptionPayments: (orgId: string | null | undefined) =>
    ['admin', 'subscription', 'payments', orgId] as QueryKey,
  /** PLURAL root: the subscription monitor list and the dashboard tile. */
  subscriptions: () => ['admin', 'subscriptions'] as QueryKey,
  subscriptionsPage: (page: number) => ['admin', 'subscriptions', page] as QueryKey,
  subscriptionsDashboard: () => ['admin', 'subscriptions', 'dashboard'] as QueryKey,
  /** Review queue root; covers the list and the per-row document queries. */
  verification: () => ['admin', 'verification'] as QueryKey,
  verificationList: () => ['admin', 'verification', 'list'] as QueryKey,
  verificationDocuments: (organizationId: string | null | undefined) =>
    ['admin', 'verification', 'documents', organizationId] as QueryKey,
} as const;

/* ------------------------------------------------------------------ */
/* Integrations (E17 US-79 — ShipStation)                             */
/* ------------------------------------------------------------------ */

/**
 * `['integrations', …]`.
 *
 * Everything the Setting > Integrations screen reads, under one root, so a
 * write there can name `integrationKeys.all` and refresh the credential list,
 * the run history, the warehouse map and the variance view together. That is
 * the right blunt instrument here: a verify rewrites the credential row, a sync
 * writes a run log AND the snapshot the variance view reads, and a warehouse
 * mapping decides which variance rows stop being stranded.
 *
 * ✅ GAP-DVS-09: NO stock key belongs on the right of an integrations write.
 * The ShipStation pull fills a snapshot table and never touches
 * `stock_by_warehouse.qty_on_hand`, so invalidating `stockKeys` after a sync
 * would be worse than useless — it would tell the next reader that a sync moves
 * stock. Closing a variance is an inventory adjustment (US-53), and that write
 * already invalidates stock through its own domain.
 */
export const integrationKeys = {
  all: ['integrations'] as const,
  /** Credential rows plus the brands with no credential yet. */
  credentials: (brandId: string) => ['integrations', 'credentials', brandId] as QueryKey,
  /**
   * One page of run history. `direction` is part of the key because it is a
   * SERVER filter, not a client one: two directions of the same page are two
   * different result sets, and leaving it out would serve the pull-only page
   * from cache the moment somebody switched the filter to pushes.
   */
  syncLogs: (brandId: string, page: number, direction: string) =>
    ['integrations', 'sync-logs', brandId, page, direction] as QueryKey,
  /**
   * The newest PULL runs, read by the credential table to fill its "Last sync"
   * column. Separate from `syncLogs` because the history tab pages and this one
   * does not: sharing a key would make one of them refetch on the other's page
   * change. See `credentials-tab.tsx` for why the column is derived at all.
   *
   * Brand sweeps only, and the key says so rather than leaving it to the
   * caller: the log also holds `reconcile_pair` runs, which read one product,
   * and archived `push_stock` runs. Neither is a sync of this brand. A key that
   * could hold either would let the two meanings share a cache entry.
   */
  latestSyncLogs: (brandId: string) =>
    ['integrations', 'sync-logs', brandId, 'latest', 'pull_inventory'] as QueryKey,
  /**
   * ONE row: the newest PULL run for `brandId`, read by the reconciliation line
   * above the /stock table (user decision 2026-09-03).
   *
   * Brand sweeps only, and the key says so rather than leaving it to the
   * caller, for the same reason `latestSyncLogs` above does: the log also holds
   * `reconcile_pair` runs, started by a PO receipt and by an approved stock
   * adjustment, and one of those reads a single product. A one-pair check
   * freshening the line that says when this whole table was last compared is
   * the failure this key shape prevents.
   *
   * ⚠ Deliberately NOT `latestSyncLogs`. That key means "a 200-row window of
   * pull runs" and belongs to the credential table; this one means "one row".
   * Two different payloads under one key would serve whichever screen mounted
   * first, and the /stock line would silently start reading a page it did not
   * ask for. Four segments, so it aliases neither `syncLogs` nor
   * `latestSyncLogs` (both five), while still sitting under the
   * `['integrations']` root that `integrationKeys.all` refreshes.
   */
  lastPullRun: (brandId: string) => ['integrations', 'last-pull-run', brandId] as QueryKey,
  warehouseMap: (brandId: string) => ['integrations', 'warehouse-map', brandId] as QueryKey,
  /**
   * The shelves inside ONE ShipStation warehouse, keyed by the MAP ROW that
   * owns it (E17 §Luồng C).
   *
   * ⚠ Read live and deliberately never held. The contract asks ShipStation at
   * the moment the question is put, because the shelves belong to the customer
   * and they rearrange them; a cached list is a picker offering a location that
   * no longer exists, and a check addressed to a dead location comes back with
   * nothing to compare. The picker therefore runs this with `staleTime: 0` and only while
   * its dialog is open.
   *
   * Its own segment rather than a child of `warehouseMap(brandId)` above: that
   * key takes a BRAND and this one takes a MAP ROW, so sharing a prefix would
   * let a refetch of one drag the other along.
   */
  warehouseMapLocations: (mapId: string) =>
    ['integrations', 'warehouse-map-locations', mapId] as QueryKey,
  /**
   * Our own active warehouses, the right-hand side of the mapping picker.
   * Module-local like `['orders','warehouses']` and `['mail-plans','warehouses']`:
   * the app has never had one shared warehouse root, and inventing a third
   * spelling of a shared one here would orphan a cache entry rather than join
   * anything.
   */
  warehouseOptions: () => ['integrations', 'warehouse-options'] as QueryKey,
  variance: (brandId: string, deferredQ: string, onlyVariances: boolean, page: number) =>
    ['integrations', 'variance', brandId, deferredQ, onlyVariances, page] as QueryKey,
  /**
   * The product-field divergence report. Under the same `['integrations']` root
   * as everything else here, so arming a brand or running a sync refreshes it
   * through `integrationKeys.all` without naming it.
   */
  productEnrichment: (brandId: string, onlyDivergent: boolean, page: number) =>
    ['integrations', 'product-enrichment', brandId, onlyDivergent, page] as QueryKey,
} as const;
